import { useRouter } from 'next/router';
import { Fragment, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import getActiveFilters from 'src/components/containers/CatalogActiveFiltersList/getActiveFilters';
import Category from 'src/components/containers/Category/Category';
import EmptyCartWatcher from 'src/components/containers/EmptyCartWatcher/EmptyCartWatcher';
import OpenGraphCategory from 'src/components/containers/OpenGraph/OpenGraphCategory';
import CategoriesHead from 'src/components/views/CategoriesHead/CategoriesHead';
import InternalServerError from 'src/components/views/Errors/500';
import Loader from 'src/components/views/Loader/Loader';
import usePrevious from 'src/hooks/usePrevious/usePrevious';
import GeneralPagesLayout from 'src/layouts/GeneralPagesLayout';
import { resetCategoryData } from 'src/redux/catalogue/catalogueActions';
import checkExcisableAvailability from 'src/redux/catalogue/catalogueOperations/checkExcisableAvailability';
import { fetchLoadMoreCategoryData } from 'src/redux/catalogue/catalogueOperations/fetchCategoryData';
import getCategoryProducts from 'src/redux/catalogue/catalogueOperations/getCategoryProducts';
import prepareCategoryDataAction from 'src/redux/catalogue/catalogueOperations/prepareCategoryData';
import { selectSelectedCategory } from 'src/redux/catalogue/selectors';
import { selectCategories } from 'src/redux/categories/categoriesSelectors';
import { useAppSelector } from 'src/redux/hooks';
import useChainService from 'src/services/chain/useChainService';
import useLocalization from 'src/services/localization/useLocalization';
import useStoreService from 'src/services/storeManagement/useStoreService';
import useToggles from 'src/services/toggles/useToggles';
import { useAppConfig } from 'src/utils/appConfig/useAppConfig';
import { GetCategorySeoMetaParams, getCategorySeoDescription, getCategorySeoTitle, getFirstLevelFilterTitle } from 'src/utils/categories/getCategorySeoMeta';
import deleteFilters from 'src/utils/filter/deleteFilters';
import getQueryWithMergedFilterParams from 'src/utils/filter/getQueryWithMergedFilterParams';
import toggleFilter from 'src/utils/filter/toggleFilter';
import makeSearchString from 'src/utils/system/makeSearchString';
import NotFoundErrorPage from '../Errors/NotFoundErrorPage';

export interface Props {
  dataPreparedOnSSR: boolean
  pageIsNotFound?: boolean
  internalServerError?: boolean
}

export interface Query {
  'category-id': string;
  lang: string;
  page: string;
  firstLevelFilter: string;
  sort: string;
}

export default function CategoryPage(props: Props) {
  const { dataPreparedOnSSR, pageIsNotFound, internalServerError } = props;

  const { AGE_CONFIRMATION_IS_ENABLED } = useToggles();
  const { id: storeId } = useStoreService();

  const dispatch = useDispatch();
  const router = useRouter<Query>();

  const { query: currentQuery } = router;
  const prevQuery = usePrevious(currentQuery);
  const { 'category-id': categoryId, page, sort, firstLevelFilter } = currentQuery;

  const selectedCategory = useAppSelector(selectSelectedCategory);
  const isFetchingProducts = useAppSelector((s) => s.catalogue.isFetchingCategoryData);
  const categoryData = useAppSelector((s) => s.catalogue.categoryData);
  const chain = useChainService();

  const excludeParams = ['lang', 'category-id', 'firstLevelFilter'];

  const canUpdateProductsData = useRef(false);

  const queryWithMergedFilterParams = getQueryWithMergedFilterParams(currentQuery);
  const searchString = makeSearchString(queryWithMergedFilterParams, ['lang', 'category-id']);
  const { currency } = useAppConfig();
  const categories = useAppSelector(selectCategories);
  const localize = useLocalization();
  const categoryDataAreNotReady = selectedCategory === null || !categoryData;

  useEffect(() => {
    async function prepareCategoryData() {
      await dispatch(prepareCategoryDataAction({
        categoryId,
        searchString,
        storeId,
      }));
    }

    dispatch(checkExcisableAvailability({
      selectedCategoryId: categoryId,
      AGE_CONFIRMATION_IS_ENABLED,
    }));

    if (!dataPreparedOnSSR) {
      prepareCategoryData();
    }

    /** @todo More dependencies break the logic. Refactoring of this component is required, including redux logic. Need to trigger fetching by user events. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, categoryId, storeId]);

  useEffect(() => {
    return () => {
      dispatch(resetCategoryData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //because changing these parameters is handled elsewhere
  canUpdateProductsData.current = prevQuery
    && prevQuery['category-id'] === categoryId
    && (prevQuery.page === page || !page || prevQuery.sort !== sort);

  useEffect(() => {
    if (canUpdateProductsData.current) {
      dispatch(getCategoryProducts(storeId, searchString));
    }
  }, [dispatch, searchString, storeId]);

  const getSingleFilterOnly = (): string => {
    const filtersQuery = { ...currentQuery };
    delete filtersQuery.page;
    delete filtersQuery.lang;
    delete filtersQuery['category-id'];
    delete filtersQuery.sort;
    delete filtersQuery.firstLevelFilter;

    const queryKeys = Object.keys(filtersQuery);
    let key: string;
    let value = '';

    if (typeof firstLevelFilter === 'string' && queryKeys.length === 0) {
      [key, value] = firstLevelFilter.split('=');
      const filters = categoryData ? categoryData.filters : [];
      const curFilterObj = filters.find(f => f.key === key);
      const curFilterOptionObj = curFilterObj
        && curFilterObj.type !== 'range'
        && curFilterObj.options.find(o => o.value === value);

      value = curFilterOptionObj ? curFilterOptionObj.name : '';
    }

    return value;
  };

  const handleFilter = (type: string, name: string) => () => {
    toggleFilter(type, name, router, excludeParams);
  };

  const getHandlerDeleteFilters = type => () => {
    deleteFilters(type, router, excludeParams);
  };

  const onPageChange = async (page: string) => {
    const {
      query: currentQuery,
    } = router;
    const queryWithMergedFilterParams = getQueryWithMergedFilterParams({ ...currentQuery, page });
    const searchString = makeSearchString(queryWithMergedFilterParams, ['lang', 'category-id']);

    await dispatch(getCategoryProducts(storeId, searchString));
  };

  const onLoadMore = async () => {
    const {
      query: currentQuery,
      asPath,
      pathname,
    } = router;

    const newQuery = {
      ...currentQuery,
      page: currentQuery.page ? Number(currentQuery.page) + 1 : 2,
    };

    const queryWithMergedFilterParams = getQueryWithMergedFilterParams(newQuery);
    const searchString = makeSearchString(queryWithMergedFilterParams, ['lang', 'category-id']);

    await dispatch(fetchLoadMoreCategoryData(storeId, searchString));

    const path = asPath.split('?')[0];

    router.push({
      pathname,
      query: newQuery,
    }, `${path}${searchString}`, { shallow: true });
  };

  if (internalServerError) {
    return (
      <GeneralPagesLayout whiteBackground>
        <InternalServerError />
      </GeneralPagesLayout>
    );
  }

  if (pageIsNotFound) {
    return <NotFoundErrorPage />;
  }

  if (categoryDataAreNotReady) {
    return (
      <GeneralPagesLayout whiteBackground>
        <Loader />
      </GeneralPagesLayout>
    );
  }

  if (Array.isArray(page)) {
    throw new Error('Page parameter is an array');
  }

  const singleFilter = getSingleFilterOnly();

  if (Array.isArray(singleFilter)) {
    throw new Error('Filter parameter is an array');
  }

  const filters = categoryData.filters;
  const products = categoryData.results;
  const count = categoryData.count;
  const countAvailable = categoryData.count_available;
  const minPrice = categoryData.stats?.price.min;
  const maxPrice = categoryData.stats?.price.max;

  const mergedQuery = getQueryWithMergedFilterParams(router.query);
  const activeFilters = getActiveFilters(mergedQuery, filters);

  const metaParams: GetCategorySeoMetaParams = {
    categories,
    selectedCategory,
    chainName: chain.name,
    firstLevelFilterTitle: getFirstLevelFilterTitle(firstLevelFilter, activeFilters),
    localize,
  };

  const seoTitle = getCategorySeoTitle(metaParams);
  const seoDescription = getCategorySeoDescription({
    ...metaParams,
    chain: chain.id,
    minPrice,
    maxPrice,
    currency,
  });

  const catalogFiltersProps = {
    withStickyFilters: true,
    showFilters: true,
    filters,
    activeFilters,
    handleFilter: handleFilter,
    isFetchingProducts,
    excludeParams,
    deleteFilters: getHandlerDeleteFilters,
    fixedMenu: false,
  };

  const pageNumber = Number(page);

  return (
    <Fragment>
      {selectedCategory && (
        <GeneralPagesLayout {...catalogFiltersProps} withNotifyGamify>
          <CategoriesHead
            page={pageNumber}
            selectedCategory={selectedCategory}
            filter={singleFilter}
            count={count}
            title={seoTitle}
            description={seoDescription}
          />
          <Category
            page={Number(page)}
            excludeParams={excludeParams}
            isFetchingProducts={isFetchingProducts}
            filters={filters}
            products={products}
            count={count}
            countAvailable={countAvailable}
            minPrice={minPrice}
            maxPrice={maxPrice}
            onLoadMore={onLoadMore}
            onPageChange={onPageChange}
          />
        </GeneralPagesLayout>
      )}

      {dataPreparedOnSSR && <OpenGraphCategory description={seoDescription} title={seoTitle} />}
      <EmptyCartWatcher />
    </Fragment>
  );
}
